import { AxiosHeaders, type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import {
  decryptEncryptedApiEnvelope,
  ENCRYPTED_API_HEADER,
  ENCRYPTED_API_HEADER_QUERY_FALLBACK,
  getSessionEncryptionKeyPair,
  isEncryptedApiEnvelope,
  requiresEncryptedApiRequest,
  type EncryptionRequestConfig,
} from './encryptedApiResponse';

function setEncryptionHeader(config: InternalAxiosRequestConfig, publicKeyBase64: string): void {
  const headers = AxiosHeaders.from(config.headers ?? {});
  headers.set(ENCRYPTED_API_HEADER, publicKeyBase64);
  config.headers = headers;
}

function isEncryptedRoute(config: InternalAxiosRequestConfig): boolean {
  return requiresEncryptedApiRequest(config.url, config.method, config.baseURL);
}

function isProtocolFailure(error: AxiosError): boolean {
  if (error.response?.status !== 400) return false;
  const data = error.response.data;
  if (!data || typeof data !== 'object') return false;
  const message = (data as { message?: unknown }).message;
  return typeof message === 'string' && message.toLowerCase().includes('protocol failure');
}

/**
 * Encrypted GET `/grantha` and `/bidhi` routes: attach session RSA public key,
 * decrypt slot-based gzip envelopes on 2xx responses.
 */
export function attachEncryptedResponseInterceptors(client: AxiosInstance): void {
  client.interceptors.request.use(
    async (config) => {
      if (!isEncryptedRoute(config)) {
        return config;
      }

      const { publicKeyBase64 } = await getSessionEncryptionKeyPair();
      const encryptionConfig = config as EncryptionRequestConfig;
      setEncryptionHeader(encryptionConfig, publicKeyBase64);

      //if (process.env.NODE_ENV === 'development') {
      console.log('[encrypted-api] request', encryptionConfig.method?.toUpperCase() ?? 'GET', encryptionConfig.url);
      //}

      if (encryptionConfig._encryptionUseQueryFallback) {
        encryptionConfig.params = {
          ...(encryptionConfig.params ?? {}),
          [ENCRYPTED_API_HEADER_QUERY_FALLBACK]: publicKeyBase64,
        };
      }

      return encryptionConfig;
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    async (response) => {
      if (response.status >= 200 && response.status < 300 && isEncryptedApiEnvelope(response.data)) {
        try {
          const { privateKey } = await getSessionEncryptionKeyPair();
          response.data = await decryptEncryptedApiEnvelope(response.data, privateKey);
          console.log(
            '[encrypted-api] decrypted',
            response.config.method?.toUpperCase() ?? 'GET',
            response.config.url,
            response.data,
          );
          if (
            response.config.url?.includes('/grantha') ||
            response.config.url?.includes('/grantha-l') ||
            response.config.url?.includes('/grantha-shloka')
          ) {
            console.log(
              'Decrypted grantha / grantha level call:',
              response.config.url,
              response.data,
            );
          }
        } catch (error) {
          console.error('[encrypted-api] decrypt failed', response.config.url, error);
          const detail = error instanceof Error ? error.message : String(error);
          return Promise.reject(new Error(`Failed to decrypt encrypted API response (${response.config.url}): ${detail}`));
        }
      }

      return response;
    },
    async (error: AxiosError) => {
      const original = error.config as EncryptionRequestConfig | undefined;
      if (
        original &&
        !original._encryptionUseQueryFallback &&
        isEncryptedRoute(original) &&
        isProtocolFailure(error)
      ) {
        original._encryptionUseQueryFallback = true;
        return client.request(original);
      }
      return Promise.reject(error);
    },
  );
}
